/**
 * Referrer linkage resolution for the admin applicant detail
 * (follow-up Item 10).
 *
 * Live defect: a referrer-added candidate (application 100020691) showed
 * "No referrer assigned to applicant." and ASSIGN REFERRER = "-- No
 * Referrer --" even though the application WAS stamped at creation. The
 * detail page joined on exactly one shape — `app.referredBy` ↔
 * `referrersList[].userId` — the strictest consumer in the system, with
 * no fallback to the `referrerId` (org doc id) / `referrerName` stamps
 * the creation paths also write.
 *
 * Canonical resolution order:
 *   1. `referrerId` → the org doc whose id matches (doc-id identity —
 *      immune to whichever userId/link shape the org doc carries);
 *   2. `referredBy` → the org doc whose userId matches (the historical
 *      join — the referrer's account uid stamped at creation);
 *   3. the creation stamps themselves (`referrerName`/`referrerId`) —
 *      the linkage displays even when the org list doesn't contain the
 *      doc (different workspace page of the list, archived org, …).
 */

export interface ReferrerLinkFields {
  referredBy?: unknown;
  referrerId?: unknown;
  referrerName?: unknown;
}

export interface ReferrerListEntry {
  id?: string;
  userId?: string;
  name?: string;
  email?: string;
  referrerId?: string;
  /** True when this entry was resolved from a deleted-referrer tombstone:
   * the organisation was permanently deleted; the linkage is history. */
  removed?: boolean;
  [key: string]: unknown;
}

/**
 * A `deletedReferrers` tombstone as served by GET /api/admin/referrers —
 * the minimal preserved record of a permanently deleted referrer
 * organisation (id, name, referral code, owner uid at deletion time).
 */
export interface DeletedReferrerTombstone {
  id?: string;
  name?: string;
  userId?: string;
  referralCode?: string;
  deletedAt?: string;
  [key: string]: unknown;
}

/** Display label for a removed referrer on preserved records. */
export function referrerRemovedLabel(name: unknown): string {
  const n = str(name).trim() || 'Referrer';
  return `${n} (organisation removed)`;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

export function resolveLinkedReferrer(
  app: ReferrerLinkFields | null | undefined,
  referrersList: ReferrerListEntry[] | null | undefined,
  deletedReferrers?: DeletedReferrerTombstone[] | null,
): ReferrerListEntry | null {
  if (!app) return null;
  const list = Array.isArray(referrersList) ? referrersList : [];
  const referrerId = str(app.referrerId);
  const referredBy = str(app.referredBy);
  const referrerName = str(app.referrerName);

  if (referrerId) {
    const byId = list.find((r) => r && (r.id === referrerId || r.referrerId === referrerId));
    if (byId) return byId;
  }
  if (referredBy) {
    const byUid = list.find((r) => r && r.userId === referredBy);
    if (byUid) return byUid;
  }
  // Deleted organisation: the live list cannot contain it (the doc is
  // gone), but its tombstone preserves the name — resolve to it so the
  // preserved linkage renders WITH the removed marker instead of as a
  // bare stamp indistinguishable from a live referrer. Live resolution
  // above always wins, so a tombstone never shadows a live org.
  const tombstones = Array.isArray(deletedReferrers) ? deletedReferrers : [];
  if (tombstones.length > 0) {
    const tomb =
      (referrerId && tombstones.find((t) => t && str(t.id) === referrerId)) ||
      (referredBy && tombstones.find((t) => t && str(t.userId) === referredBy)) ||
      null;
    if (tomb) {
      return {
        id: str(tomb.id) || referrerId,
        referrerId: str(tomb.id) || referrerId,
        userId: str(tomb.userId) || referredBy,
        name: str(tomb.name) || referrerName,
        email: '',
        inviteCode: str(tomb.referralCode),
        removed: true,
        fromTombstone: true,
      };
    }
  }
  if (referrerName || referrerId || referredBy) {
    // The org doc isn't in the list we were given — display the stamped
    // linkage rather than claiming none exists.
    return {
      id: referrerId,
      referrerId,
      userId: referredBy,
      name: referrerName,
      email: '',
      fromStamp: true,
    };
  }
  return null;
}

export interface AssignReferrerOption {
  value: string;
  label: string;
  /** True when this option was synthesised from the application's
   *  creation stamps because the referrer list does not contain an
   *  entry whose userId matches the stamped value. */
  synthetic?: boolean;
}

export interface AssignReferrerSelectModel {
  /** The value the <select> must bind so it reflects the stamped
   *  linkage — the same source of truth as the info section. */
  value: string;
  /** Options AFTER the "-- No Referrer --" placeholder. */
  options: AssignReferrerOption[];
}

/**
 * #1125 Item 3 — one source of truth for the ASSIGN REFERRER control.
 *
 * Live divergence: the REFERRER / AGENCY section named the stamped
 * referrer (via resolveLinkedReferrer's stamp fallback) while the
 * assign <select> showed "-- No Referrer --". Two causes compounded:
 *   1. the detail page fetched /api/admin/referrers WITHOUT the
 *      X-Workspace-Id header, so the server resolved the DEFAULT
 *      workspace and the list missed orgs stamped in the admin's
 *      active workspace;
 *   2. even with the right value bound, a <select> whose value matches
 *      NO <option> displays its first option — the stamped referrer's
 *      uid had no option whenever the list lacked the org (or keyed it
 *      by doc id instead of account uid).
 *
 * The model therefore guarantees: when the application is linked but no
 * list option carries the linked uid, a synthetic option representing
 * the stamped referrer is injected, so the control always reflects the
 * same resolution the section displays. Re-selecting that option is a
 * no-op reassignment (the caller preserves the stamps); picking a real
 * option or the placeholder reassigns/clears as before.
 */
export function assignReferrerSelectModel(
  app: ReferrerLinkFields | null | undefined,
  referrersList: ReferrerListEntry[] | null | undefined,
  deletedReferrers?: DeletedReferrerTombstone[] | null,
): AssignReferrerSelectModel {
  const list = Array.isArray(referrersList) ? referrersList : [];
  const options: AssignReferrerOption[] = [];
  const seen = new Set<string>();
  for (const r of list) {
    if (!r) continue;
    const value = str(r.userId);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: str(r.name) || str(r.email) || value });
  }
  const resolved = resolveLinkedReferrer(app, list, deletedReferrers);
  const value = str(resolved?.userId) || str(app?.referredBy);
  if (value && !seen.has(value)) {
    const baseLabel = str(resolved?.name) || str(app?.referrerName) || value;
    options.unshift({
      value,
      // A deleted referrer stays visible as the stamped linkage (the
      // control must reflect it), labelled as removed history — never as
      // a live, assignable organisation.
      label: resolved?.removed ? referrerRemovedLabel(baseLabel) : baseLabel,
      synthetic: true,
    });
  }
  return { value, options };
}
